import cn from "classnames";
import React, { CSSProperties, forwardRef, useId, useState } from "react";
import styles from "./Input.module.scss";

enum InputWidth {
  full = "full",
  auto = "auto",
}

type InputWidthType = `${InputWidth}`;

interface Props extends Omit<React.HTMLProps<HTMLInputElement>, "size"> {
  size?: "sm" | "md" | "lg";
  width?: InputWidthType;
  label?: string;
  error?: string;
  optional?: string;
  description?: string;
  descriptionAlignment?: "left" | "right";
  inputStyles?: CSSProperties;
}

export const Input = forwardRef<HTMLInputElement, Props>(
  (
    {
      width = "full",
      size = "md",
      label,
      id,
      type = "text",
      className,
      error,
      optional,
      style,
      description,
      descriptionAlignment = "right",
      inputStyles,
      ...props
    },
    ref
  ) => {
    const [isActive, setIsActive] = useState(false);
    const randomId = useId();
    const inputId = id || randomId;

    return (
      <div
        className={cn(
          styles.field,
          styles[`size-${size}`],
          className,
          { [styles.inputOptional]: optional },
          props.disabled && styles.disabled
        )}
      >
        {label && (
          <label htmlFor={inputId} className={styles.label}>
            {label}
          </label>
        )}
        <div
          className={cn(
            styles.inputHolder,
            error && styles.error,
            isActive && styles.active
          )}
          data-optional={optional}
        >
          <input
            ref={ref}
            id={inputId}
            type={type}
            {...props}
            className={cn(`width-${width}`, styles.input)}
            onFocus={(e) => {
              setIsActive(true);
              if (props?.onFocus) {
                props.onFocus(e);
              }
            }}
            onBlur={(e) => {
              setIsActive(false);
              if (props?.onBlur) {
                props.onBlur(e);
              }
            }}
            style={inputStyles}
          />
        </div>
        {description && !error && (
          <p
            className={cn(styles.description, cn(styles[descriptionAlignment]))}
          >
            {description}
          </p>
        )}
        {error && <p className="invalid-feedback">{error}</p>}
      </div>
    );
  }
);
